import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import debug from 'debug'
import { TOUR_STEPS, TOUR_STORAGE_KEY, TourStep } from './tour-steps'

const logger = debug('app:guided-tour')

interface TourState {
  isActive: boolean
  currentStep: number
  targetRect: DOMRect | null
  stepConfig: TourStep | null
}

const initialState: TourState = {
  isActive: false,
  currentStep: 0,
  targetRect: null,
  stepConfig: null,
}

// Shared global store so all hook instances share the same state
const listeners = new Set<() => void>()
let state = { ...initialState }

const setState = (updater: TourState | ((prev: TourState) => TourState)) => {
  state = typeof updater === 'function' ? updater(state) : updater
  listeners.forEach((l) => l())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const getSnapshot = () => state

const isCompleted = () => localStorage.getItem(TOUR_STORAGE_KEY) === 'true'
const markCompleted = () => localStorage.setItem(TOUR_STORAGE_KEY, 'true')

export const useGuidedTour = () => {
  const currentState = useSyncExternalStore(subscribe, getSnapshot)
  const rafRef = useRef<number>()

  const measureTarget = useCallback((stepIndex: number) => {
    const step = TOUR_STEPS[stepIndex]
    if (!step) return

    const el = document.querySelector(step.targetSelector) as HTMLElement | null
    logger('measureTarget step %d, selector: %s, found: %s', stepIndex, step.targetSelector, !!el)
    if (!el) return

    el.scrollIntoView({ behavior: 'smooth', block: 'center' })

    rafRef.current = requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect()
      logger('measured rect:', rect.top, rect.left, rect.width, rect.height)
      setState((prev) => ({ ...prev, targetRect: rect, stepConfig: step }))
    })
  }, [])

  const startTour = useCallback(() => {
    logger('startTour called, isCompleted:', isCompleted())
    if (isCompleted()) return
    logger('Starting tour')
    setState({ isActive: true, currentStep: 0, targetRect: null, stepConfig: null })
    setTimeout(() => measureTarget(0), 300)
  }, [measureTarget])

  const nextStep = useCallback(() => {
    const next = currentState.currentStep + 1
    if (next >= TOUR_STEPS.length) {
      markCompleted()
      setState({ ...initialState })
      return
    }
    setState((prev) => ({ ...prev, currentStep: next }))
    setTimeout(() => measureTarget(next), 100)
  }, [currentState.currentStep, measureTarget])

  const prevStep = useCallback(() => {
    const prev = currentState.currentStep - 1
    if (prev < 0) return
    setState((s) => ({ ...s, currentStep: prev }))
    setTimeout(() => measureTarget(prev), 100)
  }, [currentState.currentStep, measureTarget])

  const dismissTour = useCallback(() => {
    markCompleted()
    setState({ ...initialState })
  }, [])

  // Recalculate on resize/scroll and when the target element changes size
  useEffect(() => {
    if (!currentState.isActive) return

    const step = TOUR_STEPS[currentState.currentStep]
    if (!step) return

    const recalc = () => measureTarget(currentState.currentStep)
    window.addEventListener('resize', recalc)
    window.addEventListener('scroll', recalc, true)

    // Watch the target element for size changes (e.g. async content loading)
    const el = document.querySelector(step.targetSelector) as HTMLElement | null
    let resizeObserver: ResizeObserver | undefined
    if (el) {
      resizeObserver = new ResizeObserver(recalc)
      resizeObserver.observe(el)
    }

    return () => {
      window.removeEventListener('resize', recalc)
      window.removeEventListener('scroll', recalc, true)
      resizeObserver?.disconnect()
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [currentState.isActive, currentState.currentStep, measureTarget])

  return {
    isActive: currentState.isActive,
    currentStep: currentState.currentStep,
    targetRect: currentState.targetRect,
    stepConfig: currentState.stepConfig,
    totalSteps: TOUR_STEPS.length,
    startTour,
    prevStep,
    nextStep,
    dismissTour,
  }
}
